import type { CSSProperties, ReactNode } from 'react'

/**
 * Les squelettes de chargement — 06 §6, RG-D15.
 *
 * « Squelettes aux dimensions RÉELLES, jamais de spinner. » Un écran vide
 * laisse croire à une panne ; un texte « Chargement… » déplace le contenu au
 * moment où il arrive, et sur un réseau lent le candidat a le temps de taper à
 * l'endroit où l'écran va bouger. Un squelette occupe la place exacte de ce
 * qu'il annonce : rien ne saute quand les données arrivent.
 *
 * **Ce qu'un squelette ne dit pas à voix haute.** Des barres grises ne
 * s'annoncent pas à un lecteur d'écran. Chaque bloc porte donc `role="status"`
 * et une phrase masquée visuellement — le texte est retiré de l'ÉCRAN, pas de
 * l'application.
 *
 * L'animation s'arrête d'elle-même sous `prefers-reduced-motion` : la règle
 * globale de `index.css` la neutralise (RG-D14).
 */

interface BarreProps {
  /** Largeur CSS — `100%`, `12rem`, `40%`. */
  largeur?: string
  /** Hauteur CSS. Doit refléter la ligne réelle qu'elle remplace. */
  hauteur?: string
  ronde?: boolean
  className?: string
}

/** Une barre grise. Décorative : jamais lue, jamais focalisable. */
export function SkeletonBar({
  largeur = '100%',
  hauteur = '1rem',
  ronde = false,
  className = '',
}: BarreProps) {
  const style: CSSProperties = { width: largeur, height: hauteur, background: 'var(--surface-2)' }

  return (
    <span
      aria-hidden="true"
      className={`block animate-pulse ${ronde ? 'rounded-full' : 'rounded'} ${className}`}
      style={style}
    />
  )
}

interface ZoneProps {
  /** Ce que le lecteur d'écran annonce. Obligatoire : un squelette muet ment. */
  annonce: string
  children: ReactNode
  className?: string
}

/**
 * L'enveloppe d'un squelette.
 *
 * `aria-busy` prévient les technologies d'assistance que la zone est en cours
 * de remplissage ; `role="status"` fait annoncer la phrase une fois, sans
 * interrompre ce que l'utilisateur est en train de lire.
 */
export function SkeletonZone({ annonce, children, className = '' }: ZoneProps) {
  return (
    <div role="status" aria-busy="true" className={className}>
      <span className="sr-only">{annonce}</span>
      {children}
    </div>
  )
}

/**
 * L'arbre des thèmes en cours de chargement — F1.3.
 *
 * Les rangées reprennent la gouttière d'indentation et la pastille du bouton
 * « Suivre » : c'est ce qui évite que la liste se réorganise sous le doigt.
 */
export function TagTreeSkeleton({ lignes = 8 }: { lignes?: number }) {
  // Des largeurs variées, mais FIXES : tirées au hasard, deux rendus successifs
  // du même écran ne se ressembleraient pas, et le scintillement se voit.
  const largeurs = ['62%', '45%', '71%', '38%', '56%', '49%', '66%', '43%']
  const profondeurs = [1, 2, 2, 3, 2, 1, 2, 3]

  return (
    <SkeletonZone annonce="Chargement des thèmes…" className="flex flex-col">
      {Array.from({ length: lignes }, (_, i) => (
        <div
          key={i}
          className="flex items-center gap-2 border-b py-3"
          style={{
            borderColor: 'var(--bordure)',
            paddingLeft: `${String(((profondeurs[i % profondeurs.length] ?? 1) - 1) * 16)}px`,
          }}
        >
          <SkeletonBar largeur="2rem" hauteur="2rem" ronde className="shrink-0" />
          <SkeletonBar largeur={largeurs[i % largeurs.length] ?? '50%'} hauteur="0.9375rem" />
          <SkeletonBar largeur="5rem" hauteur="1.875rem" ronde className="ml-auto shrink-0" />
        </div>
      ))}
    </SkeletonZone>
  )
}

/** Le catalogue des concours en cours de chargement — F1.2. */
export function FacultyListSkeleton({ lignes = 6 }: { lignes?: number }) {
  const largeurs = ['30%', '68%', '35%', '74%', '28%', '61%']

  return (
    <SkeletonZone annonce="Chargement des concours…" className="flex flex-col divide-y">
      {Array.from({ length: lignes }, (_, i) => (
        <div key={i} className="flex items-start gap-3 py-4" style={{ borderColor: 'var(--bordure)' }}>
          <SkeletonBar largeur="1.25rem" hauteur="1.25rem" className="mt-1 shrink-0" />
          <span className="flex flex-1 flex-col gap-1.5">
            <SkeletonBar largeur="4.5rem" hauteur="0.9375rem" />
            <SkeletonBar largeur={largeurs[i % largeurs.length] ?? '50%'} hauteur="0.8125rem" />
            <SkeletonBar largeur="9rem" hauteur="0.8125rem" />
          </span>
        </div>
      ))}
    </SkeletonZone>
  )
}

/**
 * Le Fil en cours de chargement — 06 §6.1.
 *
 * Reprend la ligne-item : énoncé sur deux lignes, puis les options empilées.
 * C'est le squelette qui compte le plus — c'est l'écran d'accueil, et celui
 * qu'on recharge le plus souvent.
 */
export function FeedSkeleton({ cartes = 3 }: { cartes?: number }) {
  return (
    <SkeletonZone annonce="Chargement du fil…" className="flex flex-col">
      {Array.from({ length: cartes }, (_, i) => (
        <article
          key={i}
          className="flex flex-col gap-3 border-b p-4"
          style={{ borderColor: 'var(--bordure)' }}
        >
          <span className="flex items-center gap-2">
            <SkeletonBar largeur="4rem" hauteur="0.8125rem" ronde />
            <SkeletonBar largeur="3rem" hauteur="0.8125rem" ronde />
          </span>

          <span className="flex flex-col gap-2">
            <SkeletonBar hauteur="1.0625rem" />
            <SkeletonBar largeur="72%" hauteur="1.0625rem" />
          </span>

          <span className="mt-1 flex flex-col gap-2">
            {['84%', '69%', '77%', '58%'].map((l) => (
              <SkeletonBar key={l} largeur={l} hauteur="2.75rem" className="rounded-xl" />
            ))}
          </span>
        </article>
      ))}
    </SkeletonZone>
  )
}

/** L'en-tête du compte — F10. */
export function AccountSkeleton() {
  return (
    <SkeletonZone annonce="Chargement du compte…" className="flex flex-col">
      <div className="flex items-center gap-4 border-b p-4" style={{ borderColor: 'var(--bordure)' }}>
        <SkeletonBar largeur="4rem" hauteur="4rem" ronde className="shrink-0" />
        <span className="flex flex-col gap-2">
          <SkeletonBar largeur="8rem" hauteur="1.25rem" />
          <SkeletonBar largeur="14rem" hauteur="0.8125rem" />
        </span>
      </div>

      {[0, 1].map((s) => (
        <div key={s} className="flex flex-col gap-3 border-b p-4" style={{ borderColor: 'var(--bordure)' }}>
          <SkeletonBar largeur="7rem" hauteur="1.0625rem" />
          <SkeletonBar hauteur="2.75rem" className="rounded-lg" />
          <SkeletonBar largeur="60%" hauteur="0.8125rem" />
        </div>
      ))}
    </SkeletonZone>
  )
}

/**
 * La coque au tout premier chargement, avant de savoir qui est connecté.
 *
 * Volontairement sobre : à cet instant on ignore encore si le candidat verra
 * l'écran d'inscription ou son fil. Un squelette qui annoncerait le fil à un
 * visiteur non connecté serait un mensonge de plus qu'une aide.
 */
export function ShellSkeleton() {
  return (
    <SkeletonZone annonce="Chargement…" className="mx-auto max-w-[600px] px-4 pt-10">
      <div className="flex flex-col gap-4">
        <SkeletonBar largeur="2.75rem" hauteur="2.75rem" ronde />
        <SkeletonBar largeur="10rem" hauteur="2rem" ronde />
        <SkeletonBar largeur="16rem" hauteur="1rem" ronde />
      </div>
    </SkeletonZone>
  )
}

/**
 * Une liste de blocs, pour tout ce qui s'affiche en rangées.
 *
 * Le back-office et le classement en avaient chacun leur copie, en
 * `aria-hidden` : les barres se voyaient, mais un lecteur d'écran n'entendait
 * RIEN pendant le chargement — ni « en cours », ni « patientez ». Le silence
 * est indiscernable d'un écran vide, c'est-à-dire d'une panne.
 *
 * `hauteur` doit refléter la rangée réelle : c'est toute la raison d'être d'un
 * squelette (RG-D15).
 */
export function ListeSkeleton({
  annonce,
  lignes = 5,
  hauteur = '2.75rem',
}: {
  annonce: string
  lignes?: number
  hauteur?: string
}) {
  return (
    <SkeletonZone annonce={annonce} className="flex flex-col gap-3 p-4">
      {Array.from({ length: lignes }, (_, i) => (
        <SkeletonBar key={i} hauteur={hauteur} />
      ))}
    </SkeletonZone>
  )
}
